import React, { useEffect, useContext, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { removeToken } from 'lane-shared/helpers/integrations/BuildingEnginesPrism/token';
import { ContentType } from 'lane-shared/types/content/Content';
import { BEPrismTokenProvider } from 'lane-shared/contexts/BuildingEnginesPrism/useBEPrismTokenContext';
import { UserDataContext } from 'lane-shared/contexts';
import { convertToUUID } from 'uuid-encoding';

import { Loading } from 'components/general';

import Login from '../BuildingEnginesPrism/Login';
import VisitorsContainer from './components/VisitorsContainer';
import { BEPrismBuildingsContextProvider } from 'lane-shared/contexts/BuildingEnginesPrism/useBEPrismBuildingsContext';

function Fallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  useEffect(() => {
    if (error.message === '401') {
      removeToken().then(() => resetErrorBoundary());
    } else {
      resetErrorBoundary();
    }
  });

  return <Loading />;
}

type Props = {
  content: ContentType;
};

export default function BuildingEnginesPrismVisitors({ content }: Props) {
  const { user } = useContext(UserDataContext);

  const tokenRequest = useMemo(() => {
    return {
      channelIntegrationId: content?.integration?._id ? convertToUUID(content?.integration?._id) : '',
      tenantData: {
        tenant_email: user?.profile?.email || '',
        tenant_account_id:
          content?.integration?.settings?.tenantAccountId || '',
      },
    };
  }, [
    content?.integration?._id,
    user?.profile?.email,
    content?.integration?.settings?.tenantAccountId,
  ]);

  return (
    <ErrorBoundary FallbackComponent={Fallback}>
      <BEPrismTokenProvider tokenRequest={tokenRequest}>
        <BEPrismBuildingsContextProvider>
          <Login content={content}>
            <VisitorsContainer content={content} />
          </Login>
        </BEPrismBuildingsContextProvider>
      </BEPrismTokenProvider>
    </ErrorBoundary>
  );
}
